import {useEffect, useRef} from 'react';
import styled from 'styled-components';
import {HorizontalScrollArea} from '@/components/HorizentalScrollArea';
import {AnalysisTabType} from '@/map/enums';
import {palette} from '@/styles';
import {RadiusSelector} from './RadiusSelector';

interface IProps {
  activeTab: AnalysisTabType;
  radius: number;
  onTabChange(tab: {id: AnalysisTabType; label: string}): void;
  onChangeRadius(radius: number): void;
}

const TABS = [
  {id: AnalysisTabType.ai, label: 'AI 해설'},
  {id: AnalysisTabType.basic, label: '생활 인구'},
  {id: AnalysisTabType.footTraffic, label: '유동 인구'},
  {id: AnalysisTabType.customer, label: '고객 분석'},
  {id: AnalysisTabType.delivery, label: '배달 매출'},
  {id: AnalysisTabType.hallSales, label: '홀 매출'},
  {id: AnalysisTabType.stores, label: '주변 매장'},
  {id: AnalysisTabType.station, label: '대중교통'},
  {id: AnalysisTabType.hospital, label: '종합 병원'},
  {id: AnalysisTabType.childcare, label: '보육시설'},
  {id: AnalysisTabType.school, label: '학교'},
];

export const AnalysisTabs = ({
  activeTab,
  radius,
  onTabChange,
  onChangeRadius,
}: IProps) => {
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeTab || !tabsRef.current) return;

    const scrollParent = tabsRef.current;
    const tabElement = scrollParent.querySelector(`[data-id="${activeTab}"]`);

    if (tabElement) {
      const tabEl = tabElement as HTMLElement;
      const tabRect = tabEl.getBoundingClientRect();
      const parentRect = scrollParent.getBoundingClientRect();

      const tabCenter = (tabRect.left + tabRect.right) / 2;
      const parentCenter = (parentRect.left + parentRect.right) / 2;

      const distanceFromCenter = tabCenter - parentCenter;

      // 탭이 중앙에서 어느 정도 벗어났을 때만 스크롤 (ex. 10px 이상)
      if (Math.abs(distanceFromCenter) > 10) {
        const scrollOffset =
          tabEl.offsetLeft +
          tabEl.offsetWidth / 2 -
          scrollParent.clientWidth / 2;

        scrollParent.scrollTo({left: scrollOffset, behavior: 'smooth'});
      }
    }
  }, [activeTab]);

  return (
    <Root>
      <HorizontalScrollArea ref={tabsRef}>
        <Tabs>
          <RadiusSelector radius={radius} onChangeRadius={onChangeRadius} />
          {TABS.map((i) => (
            <Tab
              key={i.id}
              data-id={i.id}
              disabled={activeTab === i.id}
              onClick={() => onTabChange(i)}
            >
              {i.label}
            </Tab>
          ))}
        </Tabs>
      </HorizontalScrollArea>
    </Root>
  );
};

const Root = styled.div`
  position: relative;
`;

const Tabs = styled.div`
  padding: 12px 16px;
  gap: 8px;
  display: flex;
  white-space: nowrap;
  border-bottom: 1px solid ${palette.gray150};
`;

const Tab = styled.button`
  display: flex;
  padding: 8px 12px;
  gap: 4px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  background-color: ${palette.white};
  color: ${palette.gray500};
  border: solid 1px ${palette.gray150};
  cursor: pointer;

  &:disabled {
    background-color: ${palette.teal100};
    color: ${palette.white};
    border: solid 1px ${palette.teal100};
    cursor: default;
  }
`;
